import React, { useState, useEffect } from 'react';
import { ArrowLeft, Clock, CheckCircle, XCircle, Award, RefreshCw, Target, Brain } from 'lucide-react';
import { getQuizByEssayId, getGradeLevel, getStudyRecommendations } from '../data/quizData';

const EssayQuiz = ({ selectedEssay, onBack }) => {
  const [currentView, setCurrentView] = useState('intro');
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [timeLeft, setTimeLeft] = useState(0);
  const [isAnswered, setIsAnswered] = useState(false);
  const [showExplanation, setShowExplanation] = useState(false);
  const [quizData, setQuizData] = useState(null);
  const [results, setResults] = useState(null);

  // Load quiz data when essay is selected
  useEffect(() => {
    if (selectedEssay) {
      const quiz = getQuizByEssayId(selectedEssay.id);
      if (quiz) {
        setQuizData(quiz);
        setTimeLeft(quiz.timeLimit);
      }
    }
  }, [selectedEssay]);

  const handleTimeUp = () => {
    // Auto-submit current answer or mark as incorrect
    if (!isAnswered) {
      const newAnswer = {
        questionId: quizData.questions[currentQuestion].id,
        selectedAnswer: null,
        correctAnswer: quizData.questions[currentQuestion].correct,
        isCorrect: false,
        points: 0
      };
      setAnswers(prev => [...prev, newAnswer]);
    }
    handleQuizComplete();
  };

  // Timer countdown
  useEffect(() => {
    if (currentView === 'quiz' && timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            handleTimeUp();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [currentView, timeLeft, handleTimeUp]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleStartQuiz = () => {
    setCurrentView('quiz');
    setCurrentQuestion(0);
    setAnswers([]);
    setSelectedAnswer(null);
    setIsAnswered(false);
    setShowExplanation(false);
    setTimeLeft(quizData.timeLimit);
  };

  const handleAnswerSelect = (answerIndex) => {
    if (isAnswered) return;
    
    setSelectedAnswer(answerIndex);
    setIsAnswered(true);
    setShowExplanation(true);

    // Save answer
    const newAnswer = {
      questionId: quizData.questions[currentQuestion].id,
      selectedAnswer: answerIndex,
      correctAnswer: quizData.questions[currentQuestion].correct,
      isCorrect: answerIndex === quizData.questions[currentQuestion].correct,
      points: answerIndex === quizData.questions[currentQuestion].correct ? quizData.questions[currentQuestion].points : 0
    };

    setAnswers(prev => [...prev, newAnswer]);
  };

  const handleNextQuestion = () => {
    if (currentQuestion < quizData.questions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
      setSelectedAnswer(null);
      setIsAnswered(false);
      setShowExplanation(false);
    } else {
      handleQuizComplete();
    }
  };

  const handleQuizComplete = () => {
    const totalPoints = answers.reduce((sum, answer) => sum + answer.points, 0);
    const maxPoints = quizData.questions.reduce((sum, q) => sum + q.points, 0);
    const score = Math.round((totalPoints / maxPoints) * 100);
    const correctCount = answers.filter(a => a.isCorrect).length;
    
    const quizResults = {
      score,
      totalPoints,
      maxPoints,
      correctCount,
      totalQuestions: quizData.questions.length,
      timeSpent: quizData.timeLimit - timeLeft,
      gradeLevel: getGradeLevel(score),
      recommendations: getStudyRecommendations(score, quizData.category),
      answers
    };

    setResults(quizResults);
    setCurrentView('results');
  };

  const handleRetakeQuiz = () => {
    setCurrentView('intro');
    setCurrentQuestion(0);
    setAnswers([]);
    setSelectedAnswer(null);
    setIsAnswered(false);
    setShowExplanation(false);
    setResults(null);
    setTimeLeft(quizData.timeLimit);
  };

  if (!quizData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md mx-auto text-center">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">測驗不可用</h2>
          <p className="text-gray-600 mb-6">此作文範例暫時沒有相關測驗。</p>
          <button
            onClick={onBack}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            返回作文範例
          </button>
        </div>
      </div>
    );
  }

  // Quiz Introduction View
  if (currentView === 'intro') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="max-w-4xl mx-auto p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={onBack}
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>返回作文範例</span>
            </button>
          </div>

          {/* Quiz Info Card */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-8">
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 bg-white bg-opacity-20 rounded-xl flex items-center justify-center">
                  <Target className="w-8 h-8" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold mb-2">{quizData.title}</h1>
                  <p className="text-blue-100">{quizData.description}</p>
                </div>
              </div>
            </div>

            <div className="p-8">
              {/* Essay Info */}
              <div className="bg-gray-50 rounded-lg p-6 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">作文範例</h3>
                <p className="text-gray-700 font-medium">{selectedEssay.title}</p>
                <p className="text-sm text-gray-600 mt-1">類別：{selectedEssay.category}</p>
              </div>

              {/* Quiz Stats */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-blue-600">{quizData.totalQuestions}</div>
                  <div className="text-sm text-blue-700">題目數量</div>
                </div>
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-green-600">{Math.round(quizData.timeLimit / 60)}</div>
                  <div className="text-sm text-green-700">分鐘限時</div>
                </div>
                <div className="bg-purple-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-purple-600 capitalize">{quizData.difficulty}</div>
                  <div className="text-sm text-purple-700">難度等級</div>
                </div>
                <div className="bg-orange-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-orange-600">{quizData.questions.reduce((sum, q) => sum + q.points, 0)}</div>
                  <div className="text-sm text-orange-700">總分</div>
                </div>
              </div>

              {/* Instructions */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-8">
                <h3 className="text-lg font-semibold text-yellow-800 mb-3">測驗說明</h3>
                <ul className="text-sm text-yellow-700 space-y-2">
                  <li>• 這是一個多選題測驗，每題只有一個正確答案</li>
                  <li>• 回答後會立即顯示正確答案和解釋</li>
                  <li>• 測驗有時間限制，請合理安排時間</li>
                  <li>• 完成後會顯示詳細的成績分析和學習建議</li>
                </ul>
              </div>

              {/* Start Button */}
              <div className="text-center">
                <button
                  onClick={handleStartQuiz}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-200 transform hover:scale-105 shadow-lg"
                >
                  開始測驗
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Quiz View
  if (currentView === 'quiz') {
    const question = quizData.questions[currentQuestion];
    const progress = ((currentQuestion + 1) / quizData.questions.length) * 100;

    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-cyan-50">
        <div className="max-w-4xl mx-auto p-6">
          {/* Header */}
          <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="text-sm text-gray-600">
                  題目 {currentQuestion + 1} / {quizData.questions.length}
                </div>
                <div className="w-48 bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
              </div>
              <div className="flex items-center space-x-2 text-orange-600">
                <Clock className="w-4 h-4" />
                <span className="font-mono text-sm">{formatTime(timeLeft)}</span>
              </div>
            </div>
          </div>

          {/* Question Card */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="bg-gradient-to-r from-teal-500 to-cyan-500 text-white p-6">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                  <Brain className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold">第 {currentQuestion + 1} 題</h2>
                  <p className="text-teal-100 text-sm">{question.points} 分</p>
                </div>
              </div>
            </div>

            <div className="p-8">
              {/* Question */}
              <div className="mb-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{question.question}</h3>
              </div>

              {/* Options */}
              <div className="space-y-3 mb-6">
                {question.options.map((option, index) => (
                  <button
                    key={index}
                    onClick={() => handleAnswerSelect(index)}
                    disabled={isAnswered}
                    className={`w-full p-4 text-left rounded-lg border-2 transition-all duration-200 ${
                      selectedAnswer === index
                        ? selectedAnswer === question.correct
                          ? 'border-green-500 bg-green-50 text-green-800'
                          : 'border-red-500 bg-red-50 text-red-800'
                        : index === question.correct && showExplanation
                        ? 'border-green-500 bg-green-50 text-green-800'
                        : 'border-gray-200 bg-gray-50 hover:bg-gray-100 text-gray-700'
                    } ${isAnswered ? 'cursor-not-allowed' : 'cursor-pointer hover:border-blue-300'}`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center text-sm font-semibold ${
                        selectedAnswer === index
                          ? selectedAnswer === question.correct
                            ? 'border-green-500 bg-green-500 text-white'
                            : 'border-red-500 bg-red-500 text-white'
                          : index === question.correct && showExplanation
                          ? 'border-green-500 bg-green-500 text-white'
                          : 'border-gray-300 text-gray-500'
                      }`}>
                        {String.fromCharCode(65 + index)}
                      </div>
                      <span className="flex-1">{option}</span>
                      {selectedAnswer === index && (
                        selectedAnswer === question.correct ? (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-500" />
                        )
                      )}
                      {index === question.correct && showExplanation && selectedAnswer !== index && (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      )}
                    </div>
                  </button>
                ))}
              </div>

              {/* Explanation */}
              {showExplanation && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <h4 className="font-semibold text-blue-800 mb-2">解釋</h4>
                  <p className="text-blue-700 text-sm">{question.explanation}</p>
                </div>
              )}

              {/* Next Button */}
              {isAnswered && (
                <div className="text-center">
                  <button
                    onClick={handleNextQuestion}
                    className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    {currentQuestion < quizData.questions.length - 1 ? '下一題' : '完成測驗'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Results View
  if (currentView === 'results') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50">
        <div className="max-w-4xl mx-auto p-6">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <Award className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">測驗完成！</h1>
            <p className="text-gray-600">恭喜你完成了 {quizData.title}</p>
          </div>

          {/* Results Card */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white p-8">
              <div className="text-center">
                <div className="text-6xl font-bold mb-2">{results.score}%</div>
                <div className={`inline-block px-4 py-2 rounded-full text-sm font-semibold ${results.gradeLevel.bgColor} ${results.gradeLevel.color}`}>
                  {results.gradeLevel.label}
                </div>
              </div>
            </div>

            <div className="p-8">
              {/* Stats Grid */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-blue-600">{results.correctCount}</div>
                  <div className="text-sm text-blue-700">正確答案</div>
                </div>
                <div className="bg-red-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-red-600">{results.totalQuestions - results.correctCount}</div>
                  <div className="text-sm text-red-700">錯誤答案</div>
                </div>
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-green-600">{results.totalPoints}</div>
                  <div className="text-sm text-green-700">獲得分數</div>
                </div>
                <div className="bg-purple-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-purple-600">{Math.round(results.timeSpent / 60)}</div>
                  <div className="text-sm text-purple-700">用時(分鐘)</div>
                </div>
              </div>

              {/* Recommendations */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-8">
                <h3 className="text-lg font-semibold text-yellow-800 mb-3">學習建議</h3>
                <ul className="text-sm text-yellow-700 space-y-2">
                  {results.recommendations.map((rec, index) => (
                    <li key={index}>• {rec}</li>
                  ))}
                </ul>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={handleRetakeQuiz}
                  className="flex items-center justify-center space-x-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <RefreshCw className="w-4 h-4" />
                  <span>重新測驗</span>
                </button>
                <button
                  onClick={onBack}
                  className="flex items-center justify-center space-x-2 bg-gray-600 text-white px-6 py-3 rounded-lg hover:bg-gray-700 transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span>返回作文範例</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default EssayQuiz; 